/*
 * Copyright (c) 2022-2026 GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 */

import {Component, OnInit, signal} from '@angular/core';
import {HttpEventType, HttpResponse} from '@angular/common/http';
import {EsciComponent} from '../esci.component';
import {CommonHelperService} from '../../service/common-helper.service';

@Component({
    selector: 'app-extraction-compare',
    templateUrl: './extraction-compare.component.html',
    styleUrls: ['./extraction-compare.component.scss'],
    standalone: false
})
export class ExtractionCompareComponent extends EsciComponent implements OnInit {
  file1: File;
  file2: File;
  prolFile: File;
  showFileInput = true;
  progress = signal(0);
  permissionUrl = '/esciUI/extractionComparePermission';

  private setProgress(value: number): void {
    this.progress.set(value);
  }

  private getMaxFileSizeMessage(): string {
    if (!this.MAX_FILE_SIZE) {
      return ' Maximum file size is 20MB.';
    }
    const sizeInKB = Math.round(this.MAX_FILE_SIZE / 1000);
    const sizeInMB = Math.round(this.MAX_FILE_SIZE / (1000 * 1000) * 10) / 10;
    if (sizeInMB >= 1) {
      return ` Maximum file size is ${sizeInMB}MB.`;
    }
    return ` Maximum file size is ${sizeInKB}KB.`;
  }

  runCompare(): void {
    if (this.file1 && this.file2 && this.permission() && this.areFileSizesValid()) {
      this.showFileInput = false;
      this.setProgress(10);

      this.uploadService.uploadFilesForExtractionCompare(this.file1, this.file2, this.prolFile || null).subscribe({
        next: (event: any) => {
          if (event.type === HttpEventType.UploadProgress) {
            this.setProgress(Math.round(100 * event.loaded / event.total));
          } else if (event instanceof HttpResponse) {
            this.setProgress(0);
            // Handle blob response - trigger download
            const blob = event.body;
            const fileName = 'ExtractionCompare_' + CommonHelperService.getDateString() + '.xlsx';
            CommonHelperService.downloadFile(blob, fileName);
            this.messageService.processSuccess('Comparison report successfully generated. Please see the downloaded file.');
            this.showFileInput = true; // Allow user to upload new files
          }
        },
        error: (err: any) => {
          this.setProgress(0);
          console.log(err);
          this.messageService.processError('Could not run the extraction compare tool. Please check the files provided and try again later.');
          this.showFileInput = true;
        }
      });
    }
  }

  private areFileSizesValid(): boolean {
    let valid = true;
    const maxFileSize = this.MAX_FILE_SIZE || 20971520; // Default to 20MB if not loaded yet

    if (this.file1 && this.file1.size > maxFileSize) {
      this.messageService.processError('File 1 is too large. Please select a smaller file.' + this.getMaxFileSizeMessage());
      valid = false;
    }
    if (this.file2 && this.file2.size > maxFileSize) {
      this.messageService.processError('File 2 is too large. Please select a smaller file.' + this.getMaxFileSizeMessage());
      valid = false;
    }
    if (this.prolFile && this.prolFile.size > maxFileSize) {
      this.messageService.processError('PROL File is too large. Please select a smaller file.' + this.getMaxFileSizeMessage());
      valid = false;
    }
    return valid;
  }

  ngOnInit(): void {
    this.permission.set(false);
    // Initialize to a high default so it doesn't block before async call completes
    // The backend will update this value when the async call returns
    this.MAX_FILE_SIZE = 20971520; // 20MB temporary default until backend responds
    this.esciService.checkPermission(this);
    // Get file size limit using extraction compare URL
    this.esciService.getFileSizeLimitByUrl(this, this.esciService.extractionCompareFileSizeUrl);
  }
}

